import ClassNames from "classnames";
import { FunctionComponent, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import verticalStyles from "../stylesheets/components/header-mobile/vertical-main-menu.module.css";
import styles from "../stylesheets/components/main-menu.module.css";
import { Language, MainMenuItem, PageState } from "../types/index.js";
import MainMenuLevelCollapsible from "./MainMenuLevelCollapsible.js";

interface Props {
  menuItems: MainMenuItem[];
  parentMenuItem?: MainMenuItem;
  isPreview: boolean;
  pages: PageState;
  languageId: Language;
  onMenuItemClick: () => void;
  menuLevel: number;
  activePagePath: string[];
  fallbackLanguageId: Language | undefined;
  collapseLevel: number;
  isOpen?: boolean;
  classNames: ClassNamesProp;
}

const MainMenuListCollapsible: FunctionComponent<Props> = ({
  menuItems,
  parentMenuItem,
  isPreview,
  languageId,
  pages,
  onMenuItemClick,
  menuLevel,
  activePagePath,
  fallbackLanguageId,
  collapseLevel,
  isOpen: isOpenProp = false,
  classNames,
}) => {
  const ref = useRef<HTMLUListElement>(null);

  const isOpen = isOpenProp && menuLevel === collapseLevel + 1;

  const repeatParentItem = Boolean(
    parentMenuItem &&
    !parentMenuItem.isFolder &&
    menuLevel === collapseLevel + 1,
  );

  const combinedMenuItems = [
    ...(parentMenuItem && repeatParentItem ? [parentMenuItem] : []),
    ...menuItems,
  ];

  const list = !menuItems.length ? null : (
    <CSSTransition
      in={isOpen}
      classNames=""
      timeout={{ enter: 500, exit: 0 }}
      nodeRef={ref}
    >
      {(status) => (
        <ul
          ref={ref}
          className={ClassNames(classNames.list, {
            [classNames.listTransition]:
              menuLevel === collapseLevel + 1 &&
              (status === "entering" || status === "exiting"),
            [classNames.listExit]:
              menuLevel === collapseLevel + 1 &&
              (status === "exiting" || status === "exited"),
            [classNames.listLevel0]: menuLevel === 0,
            [classNames.listLevel1]: menuLevel === 1,
            [classNames.listLevel2up]: menuLevel >= 2,
          })}
          style={{
            height:
              status === "entering" || status === "exiting"
                ? ref.current?.scrollHeight + "px"
                : undefined,
          }}
        >
          {combinedMenuItems.map((currentMenuItem, index) => (
            <MainMenuLevelCollapsible
              key={currentMenuItem.id}
              currentMenuItem={currentMenuItem}
              isPreview={isPreview}
              languageId={languageId}
              parentId={currentMenuItem.id}
              pages={pages}
              onMenuItemClick={onMenuItemClick}
              menuLevel={menuLevel}
              activePagePath={activePagePath}
              fallbackLanguageId={fallbackLanguageId}
              collapseLevel={collapseLevel}
              isRepeatedMenuItem={repeatParentItem && index === 0}
              classNames={classNames}
            />
          ))}
        </ul>
      )}
    </CSSTransition>
  );

  return menuLevel === 0 ? <nav className={classNames.main}>{list}</nav> : list;
};

export interface ClassNamesProp {
  main: string;
  list: string;
  listTransition: string;
  listExit: string;
  listLevel0: string;
  listLevel1: string;
  listLevel2up: string;
  item: string;
  itemWrap: string;
  itemActive: string;
  itemLevel0: string;
  itemLevel1: string;
  itemLevel2up: string;
  listItem: string;
  listItemLevel0: string;
  listItemLevel1: string;
  text: string;
  expandButton: string;
  expandIcon: string;
  expandIconOpen: string;
}

// TODO: try to unify main menu and vertical main menu modules so we can use those typings implicitly instead of creating explicit ClassNamesProp interface and remapping

export const defaultClassNames: ClassNamesProp = {
  main: "", // styles.MainMenu - not in use / defined
  list: ClassNames(styles.MainMenu__List, styles["MainMenu__List--collapse"]),
  listTransition: styles["MainMenu__List--collapse-transition"],
  listExit: styles["MainMenu__List--collapse-exit"],
  listLevel0: styles["MainMenu__List--level0"],
  listLevel1: styles["MainMenu__List--level1"],
  listLevel2up: styles["MainMenu__List--level2up"],
  item: styles["MainMenu__Item"],
  itemWrap: styles["MainMenu__ItemWrap"],
  itemActive: styles["MainMenu__Item--active"],
  itemLevel0: styles["MainMenu__Item--level0"],
  itemLevel1: styles["MainMenu__Item--level1"],
  itemLevel2up: styles["MainMenu__Item--level2up"],
  listItem: styles["MainMenu__ListItem"],
  listItemLevel0: styles["MainMenu__ListItem--level0"],
  listItemLevel1: styles["MainMenu__ListItem--level1"],
  text: styles["MainMenu__Item__Text"],
  expandButton: styles["MainMenu__ExpandButton"],
  expandIcon: styles["MainMenu__ExpandIcon"],
  expandIconOpen: styles["MainMenu__ExpandIcon--open"],
};

export const verticalMenuClassNames: ClassNamesProp = {
  main: verticalStyles.VerticalMainMenu,
  list: verticalStyles.VerticalMainMenu__List,
  listTransition: verticalStyles["VerticalMainMenu__List--transition"],
  listExit: verticalStyles["VerticalMainMenu__List--exit"],
  listLevel0: "", // verticalStyles["VerticalMainMenu__List--level0"] - not in use / defined
  listLevel1: "", // verticalStyles["VerticalMainMenu__List--level1"] - not in use / defined
  listLevel2up: "", // verticalStyles["VerticalMainMenu__List--level2up"] - not in use / defined
  item: verticalStyles["VerticalMainMenu__Item"],
  itemWrap: verticalStyles["VerticalMainMenu__ItemWrap"],
  itemActive: verticalStyles["VerticalMainMenu__Item--active"],
  itemLevel0: verticalStyles["VerticalMainMenu__Item--level0"],
  itemLevel1: verticalStyles["VerticalMainMenu__Item--level1"],
  itemLevel2up: verticalStyles["VerticalMainMenu__Item--level2up"],
  listItem: "", // verticalStyles["VerticalMainMenu__ListItem"] - not in use / defined
  listItemLevel0: verticalStyles["VerticalMainMenu__ListItem--level0"],
  listItemLevel1: verticalStyles["VerticalMainMenu__ListItem--level1"],
  text: "", // verticalStyles["VerticalMainMenu__Text"] - not in use / defined
  expandButton: verticalStyles["VerticalMainMenu__ExpandButton"],
  expandIcon: verticalStyles["VerticalMainMenu__ExpandIcon"],
  expandIconOpen: verticalStyles["VerticalMainMenu__ExpandIcon--open"],
};

export default MainMenuListCollapsible;
